import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from '../../../store';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { SurveyRequest } from '../../../store/Models/Surveys/SurveyDataModel';
import * as Utilities from '../../../utilities/Utilities';
import Layout from '../../Layout';
import { LoadingSpinner, LoadingSpinnerSize } from '../../core/LoadingSpinner';
import { InventoriesGetNamesResponse } from '../../../store/Models/Api/InventoriesGetName';
import { Supplier } from '../../../store/Models/Supplier/Supplier';
import SaveDataManager from '../../../utilities/SaveDataManager';
import { OfflineProcessProgress } from '../SurveyOfflineProgressModal';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';

import "./styles.css";
import { TeamRole } from '../../../store/Models/UserContext';

export type Props = ToxnotComponentProperties &
    RouteComponentProps<{ requestId?: string }>;

enum UiState {
    Loading,
    FailedLoad,
    Editing,
    ViewingStatus,
    RequestSending
}

interface LocalState {
    uiState: UiState;
    surveyRequest: SurveyRequest | null;
    inventoryItems: InventoriesGetNamesResponse[];
    suppliers: Supplier[];
    activeSupplier: Supplier | null;
    selectedSupplierName: string;
    selectedSupplierContactEmail: string;
    offlineProcessId: string | null;
}

// ===============================================================================

class CertificationConsolidationSurveyEditor extends ToxnotSignInRequiredComponent<Props, LocalState>{
    private readonly _serviceClient: ToxnotServiceClient;
    private readonly _saveDataManager: SaveDataManager<SurveyRequest>;

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    // ===============================================================================

    public constructor(props: Props) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();
        this._saveDataManager = new SaveDataManager(this.saveDataToService, this.props.appServices);

        this.state = {
            uiState: UiState.Loading,
            surveyRequest: null,
            inventoryItems: [],
            suppliers: [],
            activeSupplier: null,
            selectedSupplierName: "",
            selectedSupplierContactEmail: "",
            offlineProcessId: null
        };
    }

    // ===============================================================================

    private enterFailedState() {
        this.setState({
            uiState: UiState.FailedLoad
        });
    }

    // ===============================================================================

    public async componentDidMount() {
        let surveyRequest: SurveyRequest | null = null;
        let inventoryIds: string[];

        if (this.props.match.params.requestId) {
            const getRequestResult = await this._serviceClient.getDataCollectionRequest(this.props.match.params.requestId);

            if (!getRequestResult.success) {
                this.enterFailedState();
                return;
            }

            surveyRequest = getRequestResult.data!.surveyRequest;

            inventoryIds = surveyRequest.requestInventoryMetadataList.map(i => i.inventoryId);
        }
        else {
            const deserializedInventoryIds = this.getCshtmlRpcParameter<string[]>();

            if (deserializedInventoryIds === null) {
                this.enterFailedState();
                return;
            }

            inventoryIds = deserializedInventoryIds;
        }

        const fetchSuppliersResult = await this._serviceClient.suppliersGet();

        if (!fetchSuppliersResult.success) {
            this.enterFailedState();
            return;
        }

        const inventoryNamesResult = await this._serviceClient.getInventoriesNames(inventoryIds);

        if (!inventoryNamesResult.success) {
            this.enterFailedState();
            return;
        }

        this.setState({
            uiState: surveyRequest !== null ? UiState.ViewingStatus : UiState.Editing,
            surveyRequest: surveyRequest,
            inventoryItems: inventoryNamesResult.data!,
            suppliers: fetchSuppliersResult.data!
        });
    }

    // ===============================================================================

    private renderSupplierContactEmailDropdown = (supplier: Supplier | null) => (
        <div className="input-group-btn">
            <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                <span className="caret"></span>
            </button>
            <ul className="dropdown-menu dropdown-menu-right">
                {supplier !== null && supplier.contactList.map((contact, i) => contact.email).sort().map((email, i) => (
                    <li key={i} onClick={e => this.supplierContactEmail_onClick(email)}><a role="button">{email}</a></li>
                ))}
            </ul>
        </div>
    );

    // ===============================================================================

    private renderEditView() {
        return (
            <div id="edit-certifications-consolidation-root">
                <p>
                    <button type="button" className="btn btn-toxnot-primary" onClick={async e => await this.sendRequestButton_click()}>{this.localizeString("Send Request")}</button>
                </p>
                <datalist id="existing-suppliers-list">
                    {this.state.suppliers.map(supplier => supplier.name).sort().map((supplierName, i) => (
                        <option key={i}>{supplierName}</option>
                    ))}
                </datalist>
                <div className="grouped-data">
                    <div className="group">
                        <div className="title">{this.localizeString("Send To")}</div>
                        <div className="content">
                            <div className="supplier-selection-form">
                                <div>
                                    <label>{this.localizeString("Company Name")}</label>
                                    <div>
                                        <input type="text" className="form-control" list={"existing-suppliers-list"} value={this.state.selectedSupplierName} onChange={e => this.supplierName_onChange(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <label>{this.localizeString("Contact Email")}</label>
                                    <div>
                                        <div className="input-group">
                                            <input type="text" className="form-control" value={this.state.selectedSupplierContactEmail} onChange={e => this.setState({ selectedSupplierContactEmail: e.target.value })} />
                                            {this.renderSupplierContactEmailDropdown(this.state.activeSupplier)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="group">
                        <div className="title">{this.localizeString("Products/Materials")}</div>
                        <div className="content">
                            <div className="inventory-list-container">
                                {this.state.inventoryItems.orderBy(item => item.name).map((item, index) => (
                                    <div className="inventory-container" key={index}>{item.name}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        let content: JSX.Element | null = null;

        if (this.state.uiState === UiState.Loading) {
            content = <LoadingSpinner size={LoadingSpinnerSize.Large} />
        }
        else if (this.state.uiState === UiState.FailedLoad) {
            content = <div>{this.localizeString("Sorry, there was a problem accessing the service.")}</div>
        }
        else if (this.state.uiState === UiState.Editing) {
            content = this.renderEditView();
        }
        else if (this.state.uiState === UiState.ViewingStatus) {
            content = <div>todo</div>
        }
        else if (this.state.uiState === UiState.RequestSending) {
            if (this.state.offlineProcessId === null) {
                content = <LoadingSpinner size={LoadingSpinnerSize.Large} />;
            }
            else {
                const notCompleteContent = (
                    <>
                        <p>{this.localizeString("3E Exchange is sending your surveys. This might take a while.")}</p>
                        <p>{this.localizeString("We'll email you when it's done, or you can come back later and check your status on the Surveys page.")}</p>
                        <p>{this.localizeString("You can safely leave this page.")}</p>
                    </>
                );

                const completeContent = (
                    <p>{this.localizeString("Your surveys have been sent!")}</p>
                );

                content = (
                    <div>
                        <OfflineProcessProgress appServices={this.props.appServices} offlineProcessId={this.state.offlineProcessId} notCompleteContent={notCompleteContent} completeContent={completeContent} />
                        <p>
                            <button type="button" className="btn btn-toxnot-primary" onClick={e => window.location.href = "/Surveys/SupplierSurveys"}>{this.localizeString("Done")}</button>
                        </p>
                    </div>
                );
            }
        }

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }}>
                <div className="tn-section">
                    <div className="tn-section-row tn-section-heading-row">
                        <div className="tn-section-col-12">
                            <h1>{this.localizeString("Send a Certification Consolidation Request")}</h1>
                        </div>
                    </div>
                    <div className="tn-section-row">
                        <div className="tn-section-col-12">
                            {content}
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    // ===============================================================================

    private saveDataToService = async (request: SurveyRequest) => {
        const apiResult = await this._serviceClient.surveyRequestUpdate(request);

        return apiResult.success;
    }

    // ===============================================================================

    //
    // UI Events
    //

    private supplierName_onChange = (nameValue: string) => {
        const existingSupplier = this.state.suppliers.firstOrDefault(s => s.name.toLowerCase().trim() == nameValue.toLowerCase().trim());

        const contactEmail = existingSupplier?.contactList[0]?.email;

        this.setState({
            selectedSupplierName: nameValue,
            selectedSupplierContactEmail: contactEmail ?? this.state.selectedSupplierContactEmail,
            activeSupplier: existingSupplier
        })
    }

    // ===============================================================================

    private supplierContactEmail_onClick = (email: string) => {
        this.setState({
            selectedSupplierContactEmail: email
        });
    }

    // ===============================================================================

    private sendRequestButton_click = async () => {
        if ((this.state.selectedSupplierName.trim().length === 0) ||
            !Utilities.isValidEmailAddress(this.state.selectedSupplierContactEmail)) {
            alert(this.localizeString("Please enter a company name and valid contact email address."));
            return;
        }

        this.setState({
            surveyRequest: null,
            offlineProcessId: null,
            uiState: UiState.RequestSending
        });

        const apiResult = await this._serviceClient.sendCertificationConsolidationSurveyRequest({
            supplierName: this.state.selectedSupplierName,
            supplierContactEmail: this.state.selectedSupplierContactEmail,
            inventoryIds: this.state.inventoryItems.map(item => item.inventoryId)
        });

        if (!apiResult.success) {
            this.enterFailedState();
            return;
        }

        this.setState({
            surveyRequest: apiResult.data!.surveyRequest,
            offlineProcessId: apiResult.data!.offlineProcessId,
            uiState: UiState.RequestSending
        });
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(withRouter(CertificationConsolidationSurveyEditor) as any);
